import React from 'react'
import * as Sentry from '@sentry/react'
import { WarningCircle, ArrowClockwise } from '@phosphor-icons/react'
import { logError } from '@/lib/log'

interface ErrorBoundaryProps {
  children: React.ReactNode
}

function ErrorFallback({ error, resetError }: { error: Error; resetError: () => void }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-cream">
      <div className="max-w-sm w-full text-center space-y-5">
        {/* İkon */}
        <div className="mx-auto w-16 h-16 rounded-full bg-red-50 flex items-center justify-center">
          <WarningCircle size={36} weight="fill" className="text-red-400" />
        </div>

        {/* Başlık */}
        <div className="space-y-2">
          <h1 className="text-xl font-black text-slate-900">Bir Sorun Oluştu</h1>
          <p className="text-sm text-slate-500 font-medium leading-relaxed">
            Beklenmedik bir hata meydana geldi. Lütfen sayfayı yenileyin veya daha sonra tekrar deneyin.
          </p>
        </div>

        {/* Hata detayı (sadece dev modunda) */}
        {import.meta.env.DEV && error?.message && (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-left">
            <p className="text-[11px] font-mono text-red-500 break-all">{error.message}</p>
          </div>
        )}

        {/* Butonlar */}
        <div className="flex flex-col gap-3">
          <div className="flex gap-3">
            <button
              onClick={resetError}
              className="flex-1 py-3.5 bg-slate-100 text-slate-600 font-bold rounded-xl active:scale-95 transition-transform flex items-center justify-center gap-2 text-sm"
            >
              <ArrowClockwise size={16} weight="bold" />
              Tekrar Dene
            </button>
            <button
              onClick={() => window.location.reload()}
              className="flex-1 py-3.5 bg-slate-800 text-white font-bold rounded-xl shadow-md active:scale-95 transition-transform text-sm"
            >
              Sayfayı Yenile
            </button>
          </div>
          
          {/* Acil Durum Sıfırlama Butonu */}
          <button
            onClick={async () => {
              try {
                const { resetToOriginal } = await import('@/services/liveUpdateService')
                await resetToOriginal()
              } catch (e) {
                logError(e)
                window.location.reload()
              }
            }}
            className="w-full py-3.5 bg-red-50 text-red-600 border border-red-200 font-bold rounded-xl active:scale-95 transition-transform text-sm"
          >
            Uygulamayı Sıfırla (Kurtarma Modu)
          </button>
        </div>

        {/* Versiyon */}
        <p className="text-[10px] text-slate-300 font-medium">
          v{import.meta.env.VITE_APP_VERSION || '?'}
        </p>
      </div>
    </div>
  )
}

export function ErrorBoundary({ children }: ErrorBoundaryProps) {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <ErrorFallback error={error as Error} resetError={resetError} />
      )}
      onError={(error) => {
        logError('[ErrorBoundary] Yakalanmayan hata:', error)
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  )
}
